import {
  Check,
  MessageSquare,
  Pencil,
  UserCog,
  X,
} from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { FindingSpine } from "./finding-spine"
import {
  ORIGIN_LABEL,
  SEVERITY_LABEL,
  VERDICT_LABEL,
  type Finding,
  type FindingSeverity,
} from "../data/review-data"

/**
 * A single finding in the rail. Renders in one of three modes — collapsed,
 * expanded, or editing — driven by props. Selection and expansion state
 * are lifted to the workspace so the PDF overlay and the rail agree on a
 * single active finding.
 */
const SEVERITY_CHIP: Record<FindingSeverity, string> = {
  error: "bg-err-tint text-err",
  warning: "bg-print-tint text-print",
  info: "bg-info-tint text-info",
}

export interface FindingRecordProps {
  finding: Finding
  selected: boolean
  expanded: boolean
  editing: boolean
  canDispose: boolean
  onSelect: () => void
  onToggleExpand: () => void
  onEnterEdit: () => void
  onCancelEdit: () => void
  onSave: (patch: { claim: string; reasoning: string | null; remediation: string | null }) => void
  onConfirm: () => void
  onDismiss: () => void
  onSendToSme: () => void
  onMarkCorrected: () => void
  onOpenComments: () => void
}

export const FindingRecord = React.forwardRef<HTMLLIElement, FindingRecordProps>(
  function FindingRecord(
    {
      finding,
      selected,
      expanded,
      editing,
      canDispose,
      onSelect,
      onToggleExpand,
      onEnterEdit,
      onCancelEdit,
      onSave,
      onConfirm,
      onDismiss,
      onSendToSme,
      onMarkCorrected,
      onOpenComments,
    },
    ref,
  ) {
    return (
      <li
        ref={ref}
        data-finding-id={finding.id}
        data-selected={selected || undefined}
        className={cn(
          "group relative overflow-hidden rounded-[10px] border bg-surface transition-[border-color,box-shadow] duration-180 ease-out",
          selected
            ? "border-text shadow-e2"
            : "border-border hover:border-border-strong hover:shadow-e1",
        )}
      >
        <button
          type="button"
          onClick={() => {
            if (!selected) onSelect()
            onToggleExpand()
          }}
          aria-expanded={expanded}
          aria-controls={`finding-body-${finding.id}`}
          className="grid w-full grid-cols-[44px_minmax(0,1fr)] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--brand)]"
        >
          <Gutter number={finding.number} severity={finding.severity} axes={finding.axes} verdictSummary={
            `${VERDICT_LABEL[finding.axes.verdict]}, ${ORIGIN_LABEL[finding.axes.origin]}`
          } />
          <div className="min-w-0 px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-id text-body-sm font-semibold text-text">
                {finding.ruleId}
              </span>
              <SeverityChip severity={finding.severity} />
              <PlacementChip finding={finding} />
              {finding.axes.origin === "carried_over" && (
                <MetaChip>Carried over from {finding.firstSeenVersion}</MetaChip>
              )}
              {finding.axes.origin === "regression" && (
                <span className="rounded-sm bg-err-tint px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-err">
                  Regression
                </span>
              )}
              {finding.authoredBy && (
                <span className="rounded-sm bg-brand-tint px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-brand">
                  Manual
                </span>
              )}
            </div>
            <p className="mt-1.5 text-body-sm leading-snug text-text">
              {finding.claim}
            </p>
          </div>
        </button>

        {expanded && (
          <div
            id={`finding-body-${finding.id}`}
            className="mx-3 mt-1 border-t border-border-soft pb-3 pt-3"
          >
            {editing ? (
              <EditForm
                finding={finding}
                onCancel={onCancelEdit}
                onSave={onSave}
              />
            ) : (
              <ReadView
                finding={finding}
                canDispose={canDispose}
                canEdit={Boolean(finding.authoredBy)}
                onEnterEdit={onEnterEdit}
                onConfirm={onConfirm}
                onDismiss={onDismiss}
                onSendToSme={onSendToSme}
                onMarkCorrected={onMarkCorrected}
                onOpenComments={onOpenComments}
              />
            )}
          </div>
        )}
      </li>
    )
  },
)

const SEVERITY_NUMBER: Record<FindingSeverity, string> = {
  error: "text-err ring-err/25",
  warning: "text-print ring-print/30",
  info: "text-info ring-info/25",
}

/**
 * 44px left column: severity-tinted number badge on a subtle top-fade,
 * then the four-axis spine below it. The number chip's ring + text tone
 * carry the severity, so a reviewer picks up position + severity in one
 * eye-fixation without needing the ERROR/WARNING chip in the body.
 * Border on the right side visually separates the identifier column
 * from the body.
 */
function Gutter({
  number,
  severity,
  axes,
  verdictSummary,
}: {
  number: number
  severity: FindingSeverity
  axes: Finding["axes"]
  verdictSummary: string
}): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="flex flex-col items-center gap-2.5 border-r border-border-soft bg-gradient-to-b from-surface-sunken/70 to-transparent to-60% py-2.5"
    >
      <span
        className={cn(
          "grid size-6 place-items-center rounded-full bg-surface font-id text-[12.5px] font-semibold leading-none tabular-nums ring-1 ring-inset",
          SEVERITY_NUMBER[severity],
        )}
      >
        {number}
      </span>
      <FindingSpine axes={axes} summary={verdictSummary} />
    </span>
  )
}

function SeverityChip({ severity }: { severity: FindingSeverity }): JSX.Element {
  return (
    <span
      className={cn(
        "rounded-sm px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em]",
        SEVERITY_CHIP[severity],
      )}
    >
      {SEVERITY_LABEL[severity]}
    </span>
  )
}

function PlacementChip({ finding }: { finding: Finding }): JSX.Element {
  const label =
    finding.placement === "box"
      ? `Boxed · p.${finding.bbox?.page ?? 1}`
      : finding.placement === "page"
        ? `Page ${finding.pageHint ?? 1}`
        : "Whole label"
  return <MetaChip>{label}</MetaChip>
}

function MetaChip({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="rounded-sm bg-surface-sunken px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-text-3">
      {children}
    </span>
  )
}

function ReadView({
  finding,
  canDispose,
  canEdit,
  onEnterEdit,
  onConfirm,
  onDismiss,
  onSendToSme,
  onMarkCorrected,
  onOpenComments,
}: {
  finding: Finding
  canDispose: boolean
  canEdit: boolean
  onEnterEdit: () => void
  onConfirm: () => void
  onDismiss: () => void
  onSendToSme: () => void
  onMarkCorrected: () => void
  onOpenComments: () => void
}): JSX.Element {
  const verdict = finding.axes.verdict
  return (
    <div className="space-y-3">
      {finding.divergence && (
        <Section label="Divergence">
          <EvidencePair evidence={finding.divergence} />
        </Section>
      )}
      {finding.reasoning && (
        <Section label="Why it is a violation">
          <p className="font-doc text-[13px] leading-[1.55] text-text-2">{finding.reasoning}</p>
        </Section>
      )}
      {finding.authorities.length > 0 && (
        <Section label="Authority">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-text-2">
            {finding.authorities.map((a, i) => (
              <React.Fragment key={`${a.kind}-${a.label}-${i}`}>
                {i > 0 && <span aria-hidden="true" className="text-text-disabled">·</span>}
                <span className="font-id text-[12px] text-text-2 underline decoration-border-strong decoration-1 underline-offset-2 hover:text-brand hover:decoration-brand">
                  {a.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </Section>
      )}
      {finding.remediation && (
        <Section label="Remediation">
          <p className="text-body-sm leading-[1.55] text-text-2">{finding.remediation}</p>
        </Section>
      )}
      {finding.confidence != null && (
        <ConfidenceBar value={finding.confidence} />
      )}

      {canDispose ? (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border-soft pt-3">
          <VerdictButton
            active={verdict === "confirmed"}
            tone="master"
            icon={<Check />}
            label="Confirm"
            onClick={onConfirm}
          />
          <VerdictButton
            active={verdict === "rejected"}
            tone="neutral"
            icon={<X />}
            label="Dismiss"
            onClick={onDismiss}
          />
          <VerdictButton
            active={verdict === "needs_sme"}
            tone="info"
            icon={<UserCog />}
            label="Send to specialist"
            onClick={onSendToSme}
          />
          {finding.axes.state === "candidate_corrected" && (
            <Button size="sm" variant="primary" onClick={onMarkCorrected}>
              Mark corrected
            </Button>
          )}
          {canEdit && (
            <IconButton
              size="sm"
              variant="ghost"
              label="Edit finding"
              icon={<Pencil />}
              onClick={onEnterEdit}
            />
          )}
          <span className="ml-auto inline-flex items-center gap-1">
            <IconButton
              size="sm"
              variant="ghost"
              label={
                finding.commentCount > 0
                  ? `Open comments, ${finding.commentCount} total`
                  : "Open comments"
              }
              icon={<MessageSquare />}
              onClick={onOpenComments}
              count={finding.unreadCount || undefined}
              countLabel="unread"
            />
          </span>
        </div>
      ) : (
        <p className="rounded-md border border-dashed border-border bg-surface-sunken/60 px-3 py-2 text-caption leading-normal text-text-3">
          Read-only — dispositions are made by reviewers and specialists.
        </p>
      )}
    </div>
  )
}

const VERDICT_TONE: Record<
  "master" | "neutral" | "info",
  { active: string; inactive: string }
> = {
  master: {
    active: "border-master bg-master-tint text-master",
    inactive: "border-border text-text-2 hover:border-master/40 hover:bg-master-tint/40 hover:text-master",
  },
  neutral: {
    active: "border-border-strong bg-surface-sunken text-text",
    inactive: "border-border text-text-2 hover:border-border-strong hover:bg-surface-sunken",
  },
  info: {
    active: "border-info bg-info-tint text-info",
    inactive: "border-border text-text-2 hover:border-info/40 hover:bg-info-tint/40 hover:text-info",
  },
}

function VerdictButton({
  active,
  tone,
  icon,
  label,
  onClick,
}: {
  active: boolean
  tone: "master" | "neutral" | "info"
  icon: React.ReactNode
  label: string
  onClick: () => void
}): JSX.Element {
  const styles = VERDICT_TONE[tone]
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7.5 items-center gap-1.5 rounded-md border px-2.5 text-body-sm font-medium transition-colors duration-120 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        active ? styles.active : styles.inactive,
      )}
    >
      <span className="[&_svg]:size-3.5" aria-hidden="true">{icon}</span>
      {label}
    </button>
  )
}

function EditForm({
  finding,
  onCancel,
  onSave,
}: {
  finding: Finding
  onCancel: () => void
  onSave: (patch: {
    claim: string
    reasoning: string | null
    remediation: string | null
  }) => void
}): JSX.Element {
  const [claim, setClaim] = React.useState(finding.claim)
  const [reasoning, setReasoning] = React.useState(finding.reasoning ?? "")
  const [remediation, setRemediation] = React.useState(finding.remediation ?? "")

  const canSave = claim.trim().length > 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSave) return
        onSave({
          claim: claim.trim(),
          reasoning: reasoning.trim() || null,
          remediation: remediation.trim() || null,
        })
      }}
      className="space-y-2.5"
    >
      <FormField label="Claim" required>
        <Input value={claim} onChange={(e) => setClaim(e.currentTarget.value)} autoFocus />
      </FormField>
      <FormField label="Reasoning">
        <Textarea
          rows={3}
          value={reasoning}
          onChange={(e) => setReasoning(e.currentTarget.value)}
        />
      </FormField>
      <FormField label="Remediation">
        <Textarea
          rows={2}
          value={remediation}
          onChange={(e) => setRemediation(e.currentTarget.value)}
        />
      </FormField>
      <div className="flex items-center gap-1.5 border-t border-border-soft pt-3">
        <Button type="submit" size="sm" variant="primary" disabled={!canSave}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * Two-row evidence: master on top with a subtle green tint, printed below
 * with an amber tint. The eyebrow column sits at a fixed 78px, so quoted
 * text on the right aligns cleanly across findings.
 */
function EvidencePair({
  evidence,
}: {
  evidence: { master: string | null; printed: string | null }
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <EvidenceRow label="Master" tone="master" text={evidence.master} />
      <EvidenceRow label="Printed" tone="print" text={evidence.printed} />
    </div>
  )
}

function EvidenceRow({
  label,
  tone,
  text,
}: {
  label: string
  tone: "master" | "print"
  text: string | null
}): JSX.Element {
  const [labelBg, labelFg] =
    tone === "master"
      ? ["bg-master-tint", "text-master"]
      : ["bg-print-tint", "text-print"]
  const contentBg =
    tone === "master" ? "bg-master-tint/40" : "bg-print-tint/40"
  return (
    <div className="grid grid-cols-[78px_minmax(0,1fr)] border-b border-border-soft last:border-b-0">
      <span
        className={cn(
          "px-2.5 py-2 font-id text-[10.5px] font-medium uppercase tracking-[0.08em]",
          labelBg,
          labelFg,
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 whitespace-pre-wrap break-words px-3 py-2 font-doc text-[15px] leading-[1.4] text-text",
          contentBg,
        )}
      >
        {text ?? (
          <span className="text-body-sm italic text-text-3">(absent)</span>
        )}
      </span>
    </div>
  )
}

/**
 * Confidence bar. Fill tone reflects the pipeline's certainty band —
 * high reads as trustworthy ink, medium as neutral graphite, low as
 * amber caution. We deliberately do NOT surface `kind` (mechanical /
 * judgment / hybrid) — that is a rule-authoring taxonomy, not a
 * reviewer-decision axis, and cluttering the row with it degrades scan.
 */
function ConfidenceBar({
  value,
  className,
}: {
  value: number
  className?: string
}): JSX.Element {
  const pct = Math.round(value * 100)
  const fill =
    pct >= 90 ? "bg-text-2" : pct >= 70 ? "bg-text-3" : "bg-print"
  return (
    <div
      className={cn(
        "flex items-center gap-2 font-id text-[10.5px] uppercase tracking-[0.08em] text-text-3",
        className,
      )}
    >
      <span className="tabular-nums">{pct}% confidence</span>
      <span
        className="block h-[3px] w-9 overflow-hidden rounded-full bg-border"
        aria-hidden="true"
      >
        <span className={cn("block h-full", fill)} style={{ width: `${pct}%` }} />
      </span>
    </div>
  )
}

function Section({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <span className="font-id text-[10.5px] font-medium uppercase tracking-[0.1em] text-text-3">
        {label}
      </span>
      <div>{children}</div>
    </div>
  )
}
